import { useState } from 'react';
import { Section } from '~/components/layout';
import { ButtonLink, Grid } from '~/components/ui';
import { payrollSavings as content } from '~/content/payroll';
import { cn } from '~/lib/cn';

const MONTHS = 12;
const usd = (value: number) => `$${value.toLocaleString('en-US')}`;
/** Table cells use the Figma's spaced "$ 3,000" style. */
const amount = (value: number) => `$ ${value.toLocaleString('en-US')}`;
/** Centre of the 76px value chip for a 0–1 slider position, inset so it never overhangs. */
const thumbCentre = (position: number) => `calc(38px + (100% - 76px) * ${position})`;

/**
 * Typical provider vs nsave yearly payroll cost, driven by a team-size slider
 * and presets (Figma 1269:46720 / 1269:46761, mobile 1269:47317).
 */
export function PayrollSavings({ className }: { className?: string }) {
  const [employees, setEmployees] = useState(content.defaultEmployees);

  const transferTotal = employees * MONTHS * content.providerTransferFee;
  const subscriptionTotal = employees * MONTHS * content.providerSubscription;
  const yearlySavings = transferTotal + subscriptionTotal;
  const progress = (employees - 1) / (content.maxEmployees - 1);

  return (
    <Section className={className}>
      <Grid className="gap-y-0 lg:grid-rows-[auto_1fr]">
        <div className="col-span-9 lg:col-span-4 lg:col-start-6 lg:row-start-1">
          <div className="border-b border-black-6 pb-8">
            <h2 className="text-[40px] leading-[1.16] font-semibold tracking-[-0.88px] text-grey-1000">
              <span className="text-grey-500">Save</span> {usd(yearlySavings)}/y
              <br />
              <span className="text-grey-500">on your payroll</span>
            </h2>
          </div>

          <div className="flex gap-3.5 border-b border-black-6 py-5 md:gap-4">
            {content.presets.map((preset) => {
              const active = employees === preset.employees;
              return (
                <button
                  key={preset.label}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setEmployees(preset.employees)}
                  className={cn(
                    'cursor-pointer rounded-pill px-3 pt-[11px] pb-3 text-[13px] leading-[1.4] font-medium tracking-[0.15px] whitespace-nowrap transition-colors md:px-3.5 md:pt-3 md:pb-[13px] md:text-[16px] md:tracking-[0.192px]',
                    active
                      ? 'bg-grey-100 text-grey-900'
                      : 'border-[1.2px] border-grey-50 bg-white text-black hover:bg-grey-25',
                  )}>
                  {preset.label}
                </button>
              );
            })}
          </div>

          <label
            htmlFor="payroll-team-size"
            className="mt-[60px] block text-h4 font-semibold text-black md:mt-[70px]">
            {content.teamSizeLabel}
          </label>
          {/* The visible track, fill and value chip are decorative; the transparent
              native range input on top provides pointer, keyboard and a11y behaviour.
              The chip centre runs from 38px to (100% - 38px) so it never overhangs,
              and the native thumb is sized to the chip (76px) so it travels the same
              range — pressing the chip grabs it instead of jumping the value. */}
          <div className="relative mt-[65px] h-[37px]">
            <div className="absolute inset-x-0 top-[15px] h-2 rounded-[20px] bg-grey-50" />
            <div
              className="absolute start-0 top-[15px] h-2 rounded-[20px] bg-red-500"
              style={{ width: thumbCentre(progress) }}
            />
            <div
              aria-hidden
              className="pointer-events-none absolute top-0 flex h-[37px] w-[76px] -translate-x-1/2 items-center justify-center gap-2.5 rounded-pill border-[1.2px] border-grey-50 bg-white text-[16px] leading-[1.4] font-medium tracking-[0.192px] text-black tabular-nums"
              style={{ left: thumbCentre(progress) }}>
              <img
                src="/images/payroll/icons/slider-chevron.svg"
                alt=""
                className="h-2.5 w-1 rotate-180"
              />
              {employees}
              <img src="/images/payroll/icons/slider-chevron.svg" alt="" className="h-2.5 w-1" />
            </div>
            <input
              id="payroll-team-size"
              type="range"
              min={1}
              max={content.maxEmployees}
              step={1}
              value={employees}
              aria-valuetext={`${employees} employees`}
              onChange={(event) => setEmployees(Number(event.target.value))}
              className="absolute inset-0 size-full cursor-pointer appearance-none bg-transparent opacity-0 [&::-moz-range-thumb]:h-[37px] [&::-moz-range-thumb]:w-[76px] [&::-moz-range-thumb]:border-0 [&::-webkit-slider-thumb]:h-[37px] [&::-webkit-slider-thumb]:w-[76px] [&::-webkit-slider-thumb]:appearance-none"
            />
          </div>
          {/* Tick labels sit at the same positions the chip uses for their values. */}
          <div
            aria-hidden
            className="relative mt-2.5 h-[22px] text-[16px] leading-[1.4] font-medium tracking-[0.192px] text-grey-900">
            {[1, Math.round(content.maxEmployees / 2), content.maxEmployees].map((tick) => (
              <span
                key={tick}
                className="absolute top-0 -translate-x-1/2"
                style={{ left: thumbCentre((tick - 1) / (content.maxEmployees - 1)) }}>
                {tick}
              </span>
            ))}
          </div>
        </div>

        <div className="col-span-9 mt-[75px] flex flex-col gap-10 lg:col-span-4 lg:col-start-1 lg:row-span-2 lg:row-start-1 lg:mt-0 lg:gap-12">
          <CostTable
            title={content.providerTitle}
            employees={employees}
            fee={content.providerTransferFee}
            subscription={content.providerSubscription}
          />
          <CostTable title={content.nsaveTitle} employees={employees} fee={0} subscription={0} />
        </div>

        {/* Hidden on phones only (the mobile design ends the section at the tables),
            on the same `md` breakpoint the page's phone copy switches at. */}
        <div className="col-span-9 mt-10 hidden md:block lg:col-span-4 lg:col-start-6 lg:row-start-2 lg:mt-[70px]">
          <div className="flex flex-wrap gap-4">
            <ButtonLink href={content.primaryCta.href} external={content.primaryCta.external}>
              {content.primaryCta.label}
            </ButtonLink>
            <ButtonLink
              href={content.secondaryCta.href}
              external={content.secondaryCta.external}
              variant="secondary">
              {content.secondaryCta.label}
            </ButtonLink>
          </div>
          <p className="mt-[51px] text-[13px] leading-[1.32] text-grey-500">
            {content.savingsNote(usd(yearlySavings), employees)}
          </p>
        </div>
      </Grid>
    </Section>
  );
}

function CostTable({
  title,
  employees,
  fee,
  subscription,
}: {
  title: string;
  employees: number;
  fee: number;
  subscription: number;
}) {
  const { columns, rows } = content;
  const lines = [
    { label: rows.transferFee, fee },
    { label: rows.subscription, fee: subscription },
  ];
  const total = employees * MONTHS * (fee + subscription);
  // Desktop shows employees and months as their own columns; mobile folds them
  // into a caption under the row label.
  const detailCell = 'hidden lg:table-cell';

  return (
    <div>
      <h3 className="text-h4 font-semibold text-black">{title}</h3>
      <table className="mt-8 w-full table-fixed bg-grey-25 text-[15px] leading-[1.32] tracking-[0.27px] text-black">
        <thead>
          <tr className="h-14 text-start font-semibold">
            <th className="w-[56%] lg:w-[33%]">
              <span className="sr-only">Cost</span>
            </th>
            <th className={cn(detailCell, 'w-[20%] text-start font-semibold')}>
              {columns.employees}
            </th>
            <th className={cn(detailCell, 'w-[15.5%] text-start font-semibold')}>
              {columns.months}
            </th>
            <th className="w-[21.5%] text-start font-semibold lg:w-[14.3%]">{columns.fee}</th>
            <th className="text-start font-semibold">{columns.total}</th>
          </tr>
        </thead>
        <tbody className="bg-white">
          {lines.map((line) => (
            <tr key={line.label} className="h-14 border-b border-grey-100">
              <th scope="row" className="ps-0.5 text-start font-semibold lg:ps-0">
                {line.label}
                <span className="block text-[10px] font-normal tracking-[0.18px] text-grey-500 lg:hidden">
                  {content.rowCaption(employees, MONTHS)}
                </span>
              </th>
              <td className={detailCell}>{employees}</td>
              <td className={detailCell}>{MONTHS}</td>
              <td>{amount(line.fee)}</td>
              <td>{amount(employees * MONTHS * line.fee)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="h-14 font-semibold">
            <td colSpan={2} className="lg:hidden" />
            <td colSpan={4} className="hidden lg:table-cell" />
            <td>{amount(total)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
